const DRIVE = 'https://www.googleapis.com/drive/v3/files';
const DRIVE_UPLOAD = 'https://www.googleapis.com/upload/drive/v3/files';

type TokenClientConfig = {
  client_id: string;
  scope: string;
  hd?: string;
  login_hint?: string;
  callback(response: { access_token?: string; expires_in?: number; error?: string }): void;
  error_callback?(error: { type?: string }): void;
};

declare global {
  interface Window {
    google?: { accounts: { oauth2: { initTokenClient(config: TokenClientConfig): { requestAccessToken(options?: { prompt?: string }): void } } } };
  }
}

export type UploadedDrivePhoto = { id: string; name: string; webViewLink?: string };

export function requestFitnessDriveToken(clientId: string, ownerEmail: string): Promise<{ token: string; expiresAt: number }> {
  if (!window.google?.accounts.oauth2) return Promise.reject(new Error('Google Identity Services did not load. Refresh and try again.'));
  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'openid email https://www.googleapis.com/auth/drive.file',
      hd: 'caydenlasher.co',
      login_hint: ownerEmail,
      callback: (response) => response.access_token
        ? resolve({ token: response.access_token, expiresAt: Date.now() + (response.expires_in ?? 3600) * 1000 })
        : reject(new Error(response.error ?? 'Google authorization failed.')),
      error_callback: (error) => reject(new Error(error.type ?? 'Google authorization was interrupted.')),
    });
    client.requestAccessToken({ prompt: 'consent' });
  });
}

export class FitnessDriveAdapter {
  constructor(private token: string, private expiresAt: number, private ownerEmail: string) {}

  async verifyOwner() {
    const identity = await this.parse<{ email?: string; email_verified?: boolean }>(await this.request('https://openidconnect.googleapis.com/v1/userinfo'));
    if (!identity.email_verified || identity.email?.toLowerCase() !== this.ownerEmail.toLowerCase()) {
      throw new Error(`Choose ${this.ownerEmail} for fitness photos and backups.`);
    }
  }

  async ensurePhotoFolder(date: Date, configuredRootId = ''): Promise<{ id: string; path: string }> {
    let parent = configuredRootId;
    if (!parent) {
      parent = await this.findOrCreateFolder('Personal OS', 'root');
      parent = await this.findOrCreateFolder('Fitness', parent);
      parent = await this.findOrCreateFolder('Body Composition', parent);
    }
    const year = String(date.getFullYear());
    const month = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    parent = await this.findOrCreateFolder(year, parent);
    parent = await this.findOrCreateFolder(month, parent);
    return { id: parent, path: `Personal OS/Fitness/Body Composition/${year}/${month}` };
  }

  async uploadPhoto(file: File, parentId: string, name: string): Promise<UploadedDrivePhoto> {
    const boundary = `fitness_${crypto.randomUUID().replace(/-/g, '')}`;
    const payload = new Blob([
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
      JSON.stringify({ name, parents: [parentId] }),
      `\r\n--${boundary}\r\nContent-Type: ${file.type || 'application/octet-stream'}\r\n\r\n`,
      file,
      `\r\n--${boundary}--\r\n`,
    ], { type: `multipart/related; boundary=${boundary}` });
    return this.parse(await this.request(`${DRIVE_UPLOAD}?uploadType=multipart&fields=id,name,webViewLink`, {
      method: 'POST', headers: { 'Content-Type': payload.type }, body: payload,
    }));
  }

  private async findOrCreateFolder(name: string, parentId: string): Promise<string> {
    const q = encodeURIComponent(`name = '${name.replaceAll("'", "\\'")}' and '${parentId}' in parents and mimeType = 'application/vnd.google-apps.folder' and trashed = false`);
    const existing = await this.parse<{ files?: Array<{ id: string }> }>(await this.request(`${DRIVE}?q=${q}&fields=files(id)&pageSize=1`));
    if (existing.files?.[0]) return existing.files[0].id;
    const created = await this.parse<{ id: string }>(await this.request(`${DRIVE}?fields=id`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, mimeType: 'application/vnd.google-apps.folder', parents: [parentId] }),
    }));
    return created.id;
  }

  private request(url: string, init: RequestInit = {}) {
    if (Date.now() >= this.expiresAt - 30_000) throw new Error('Google authorization expired. Reconnect and try again.');
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${this.token}`);
    return fetch(url, { ...init, headers });
  }

  private async parse<T>(response: Response): Promise<T> {
    if (response.ok) return response.json() as Promise<T>;
    let message = `Google Drive request failed (${response.status}).`;
    try { message = ((await response.json()) as { error?: { message?: string } }).error?.message ?? message; } catch { /* empty response */ }
    throw new Error(message);
  }
}
